import { useEffect, useRef, useState } from 'react';

import { Aggregated, PostcodeMarker } from '../CountryView/types';
import { Incident, pointInFeature } from '../MapSharedData';
import { PostcodeEntry, PostcodeIndex, lookupPostcode } from '../parseGeonames';

interface UseUnifiedAggregationOptions {
  activeCountry: string | null;
  incidents: Incident[];
  postcodes: PostcodeIndex | null;
  regionsGeoJson: GeoJSON.FeatureCollection | null;
  regionNameProperty: string;
}

interface AggregationResult {
  aggregated: Aggregated;
  allMarkers: PostcodeMarker[];
  unmatchedCount: number;
  /**
   * Country the aggregation was computed for. Lets downstream hooks reject
   * stale data when activeCountry has already moved on but the upstream
   * geojson fetch hasn't finished. Null on the world view.
   */
  country: string | null;
}

const EMPTY: AggregationResult = {
  aggregated: {},
  allMarkers: [],
  unmatchedCount: 0,
  country: null,
};

/**
 * Builds the per-region aggregation for the active country. Mirrors the
 * legacy CountryView useAggregation but tracks the country alongside the
 * result so the persistent UnifiedMap can verify freshness — when the user
 * jumps between countries faster than geojson loads, downstream hooks need a
 * way to ignore the briefly-stale state.
 */
export function useUnifiedAggregation({
  activeCountry,
  incidents,
  postcodes,
  regionsGeoJson,
  regionNameProperty,
}: UseUnifiedAggregationOptions): AggregationResult {
  const [result, setResult] = useState<AggregationResult>(EMPTY);

  // Cache of postcode → resolution (GeoNames entry + region name). This is the
  // expensive part (a point-in-polygon scan over every region per postcode)
  // and it depends ONLY on the postcode string, the GeoNames index and the
  // regions geometry — never on incident counts. So when the incident filter
  // changes counts but the country/geometry stays put, we reuse it instead of
  // rescanning. Reset whenever any of those identity inputs change.
  const cacheRef = useRef<{
    postcodes: PostcodeIndex | null;
    regionsGeoJson: GeoJSON.FeatureCollection | null;
    regionNameProperty: string;
    activeCountry: string | null;
    resolved: Map<string, { entry: PostcodeEntry; regionName: string } | null>;
  } | null>(null);

  useEffect(() => {
    if (!activeCountry || !postcodes || !regionsGeoJson) {
      setResult(EMPTY);
      return;
    }

    let cache = cacheRef.current;
    if (
      !cache ||
      cache.postcodes !== postcodes ||
      cache.regionsGeoJson !== regionsGeoJson ||
      cache.regionNameProperty !== regionNameProperty ||
      cache.activeCountry !== activeCountry
    ) {
      cache = {
        postcodes,
        regionsGeoJson,
        regionNameProperty,
        activeCountry,
        resolved: new Map(),
      };
      cacheRef.current = cache;
    }
    const resolved = cache.resolved;

    /** Resolve a postcode to its GeoNames entry + region, memoised per code. */
    const resolve = (rawCode: string) => {
      const cached = resolved.get(rawCode);
      if (cached !== undefined) return cached;
      const entry = lookupPostcode(postcodes, rawCode);
      if (!entry) {
        resolved.set(rawCode, null);
        return null;
      }
      const feature = regionsGeoJson.features.find((f) =>
        pointInFeature([entry.lng, entry.lat], f),
      );
      const regionName = feature?.properties?.[regionNameProperty] ?? 'Unassigned';
      const hit = { entry, regionName };
      resolved.set(rawCode, hit);
      return hit;
    };

    const regions: Aggregated = {};
    const markers: PostcodeMarker[] = [];
    let unmatched = 0;

    // Region rate denominator, summed over EVERY postcode that received orders —
    // including postcodes with NO incident (count 0). This dilutes the region
    // rate by the full order volume, the same way the country-level rate divides
    // by all orders sent; without it a region is divided only by its incident
    // postcodes and reads ~100%. Kept separate from `regions` so count-0
    // postcodes only feed the denominator — they never become markers, sidebar
    // rows, or standalone (incident-free) regions.
    const regionSent: Record<string, number> = {};

    for (const incident of incidents) {
      const rawCode = incident.postcode;
      const hasCode = typeof rawCode === 'string' && rawCode.trim() !== '';
      const hit = hasCode ? resolve(rawCode as string) : null;
      const regionName = hit ? hit.regionName : 'Unassigned';

      if (incident.sent != null) {
        regionSent[regionName] = (regionSent[regionName] ?? 0) + incident.sent;
      }

      // Numerator + everything drawn: only postcodes that actually had an incident.
      if (incident.count <= 0) continue;

      const marker: PostcodeMarker = {
        postcode: hasCode ? (rawCode as string) : '—',
        place: hit ? hit.entry.place : hasCode ? 'Unknown location' : 'No postcode',
        count: incident.count,
        sent: incident.sent,
        region: regionName,
        lng: hit ? hit.entry.lng : NaN,
        lat: hit ? hit.entry.lat : NaN,
        incidents: incident.incidents,
      };

      if (hit) markers.push(marker);
      else unmatched++;

      if (!regions[regionName]) regions[regionName] = { total: 0, postcodes: [] };
      regions[regionName].total += incident.count;
      regions[regionName].postcodes.push(marker);
    }

    // Attach the full-volume denominator to each region that has incidents.
    for (const name in regions) {
      if (regionSent[name] != null) regions[name].sent = regionSent[name];
    }

    setResult({
      aggregated: regions,
      allMarkers: markers,
      unmatchedCount: unmatched,
      country: activeCountry,
    });
  }, [activeCountry, incidents, postcodes, regionsGeoJson, regionNameProperty]);

  return result;
}
